/**
 * Bind interpreter — turns a DB-registered tool spec (core 0033 `core.mcp_tool`)
 * into an executable tool. A module declares its agent verbs as DATA; a surface
 * reads them via client.describeTools() and builds the handler here.
 *
 * This lives in the SDK, not in the MCP connector, because it is not an MCP
 * concern: `McpToolSpec` is already an SDK type, and there is more than one
 * surface that has to run these verbs — the MCP connector for external agents,
 * and the console copilot for the admin sitting in the UI. Two interpreters
 * would drift, and a bind that means one thing to an agent and another thing to
 * the copilot is precisely the class of bug core 0102 was written about.
 *
 * Bind kinds:
 *   rpc     — call one governed RPC: {schema, fn, args, returns}
 *   entity  — create an entity row via the SDK: {entity, values, returns}
 *   builtin — a composite handler that lives as code, referenced by name
 *             (for the few tools that fan out across several calls)
 *
 * Arg specs build the RPC/entity payload from the tool's input args:
 *   {"from":"quote_id"}            -> args.quote_id
 *   {"from":"qty","default":1}     -> args.qty ?? 1
 *   {"const":0}                    -> literal 0
 * Return specs shape the result:
 *   omitted                        -> the raw scalar / row
 *   {"as":"total"}                 -> {total: scalar}
 *   {"literal":{"ok":true}}        -> that object (scalar ignored)
 *
 * Every bind re-checks module entitlement before running — belt-and-suspenders
 * over the entitlement filter in core.describe_tools() (and the RLS floor
 * below, which is the actual enforcement: none of this grants anything).
 */
import type { SolumClient, McpToolSpec } from './client.js'

type ArgSpec = { from?: string; const?: unknown; default?: unknown }
/** `entity` is host navigation metadata; it never changes the returned value. */
type ReturnSpec = { as?: string; literal?: Record<string, unknown>; entity?: string }
type RpcBind = { kind: 'rpc'; schema: string; fn: string; args?: Record<string, ArgSpec>; returns?: ReturnSpec }
type EntityBind = { kind: 'entity'; entity: string; op?: 'create'; values?: Record<string, ArgSpec>; returns?: ReturnSpec }
type BuiltinBind = { kind: 'builtin'; ref: string }
type Bind = RpcBind | EntityBind | BuiltinBind

/** An executable tool compiled from a registered spec. */
export interface SolumTool {
  name: string
  description: string
  inputSchema: Record<string, unknown> // JSON Schema (the MCP tools/list contract)
  mutates: boolean                      // withheld from a read-only persona
  requiresConfirmation?: boolean        // proposal-only until a governed human act confirms
  requiresApproval?: boolean
  policyEffect?: 'allow' | 'confirm' | 'approval' | 'deny'
  /** Policy metadata; it grants nothing and is re-resolved at call time. */
  module?: string
  policyAction?: string
  policyEntityArgument?: string
  policyModuleArgument?: string
  handler(client: SolumClient, args: Record<string, unknown>): Promise<unknown>
}

/** A composite/coded handler a `builtin` bind resolves to (keyed by ref). */
export type BuiltinHandler = (client: SolumClient, args: Record<string, unknown>) => Promise<unknown>

function buildArgs(spec: Record<string, ArgSpec> | undefined, args: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, s] of Object.entries(spec ?? {})) {
    if ('const' in s) { out[key] = s.const ?? null; continue }
    const v = s.from !== undefined ? args[s.from] : undefined
    if (v !== undefined) out[key] = v
    else if ('default' in s) out[key] = s.default            // explicit bind default (may be null)
    // else: OMIT the key entirely — so the RPC's own SQL DEFAULT applies
  }
  return out
}

function shapeReturn(spec: ReturnSpec | undefined, value: unknown): unknown {
  if (!spec) return value
  if (spec.literal !== undefined) return spec.literal
  if (spec.as) return { [spec.as]: value }
  return value
}

/**
 * Platform infrastructure — never licensable.
 *
 * `core` was always exempt. `access` has to be too: it carries the verbs that
 * administer who can see what, and core 0115 makes the same exemption on the
 * visibility side (describe_tools). Splitting the two gates is what produced the
 * bug this comment exists for — the rehearsal made the verbs VISIBLE and they
 * still failed here with "module 'access' is not entitled".
 *
 * The licensing model promises to degrade a module to read-only and never brick
 * the deployment. Losing access administration is worse than read-only in the
 * one direction that matters: it removes the ability to REVOKE, at exactly the
 * moments — incident, departure, licence lapse — when you need it.
 *
 * Exempting a module from entitlement grants nothing: every verb still gates
 * itself server-side, so an unprivileged caller gets an honest refusal instead
 * of a missing tool.
 */
const ALWAYS_AVAILABLE = new Set(['core', 'access'])

async function requireAvailable(client: SolumClient, module: string, mutates: boolean): Promise<void> {
  if (!ALWAYS_AVAILABLE.has(module) && !(await client.mcpModuleAvailable(module, mutates))) {
    throw new Error(`module '${module}' is not entitled for ${mutates ? 'mutation' : 'read'}`)
  }
}

/** Compile a DB tool spec into an executable tool by interpreting its bind. */
export function specToTool(spec: McpToolSpec, builtins: Record<string, BuiltinHandler> = {}): SolumTool {
  const bind = spec.bind as unknown as Bind
  const base = {
    name: spec.name,
    description: spec.description,
    inputSchema: spec.inputSchema,
    mutates: spec.mutates,
    requiresConfirmation: spec.requiresConfirmation === true,
    requiresApproval: spec.requiresApproval === true,
    policyEffect: spec.policyEffect,
    module: spec.module,
    policyAction: spec.name,
  }

  if (bind.kind === 'builtin') {
    const fn = builtins[bind.ref]
    if (!fn) throw new Error(`no builtin handler '${bind.ref}' for tool '${spec.name}'`)
    return {
      ...base,
      handler: async (client, args) => {
        await requireAvailable(client, spec.module, spec.mutates)
        return fn(client, args)
      },
    }
  }

  if (bind.kind === 'entity') {
    return {
      ...base,
      handler: async (client, args) => {
        await requireAvailable(client, spec.module, spec.mutates)
        const handle = await client.entity(bind.entity)
        if (!handle) throw new Error(`entity '${bind.entity}' is not available`)
        return shapeReturn(bind.returns, await handle.create(buildArgs(bind.values, args)))
      },
    }
  }

  // rpc (default)
  return {
    ...base,
    handler: async (client, args) => {
      await requireAvailable(client, spec.module, spec.mutates)
      const scalar = await client.raw().rpcScalar(bind.fn, buildArgs(bind.args, args), bind.schema)
      return shapeReturn(bind.returns, scalar)
    },
  }
}
